import { Component, signal, inject, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ActionModal } from './shared/components/action-modal/action-modal';
import { Toast } from './shared/components/toast/toast';
import { SplashScreen } from './shared/components/splash-screen/splash-screen';
import { selectActionModal, selectInfoModal } from './core/store/modal.selectors';
import { hideActionModal, hideInfoModal, showToast } from './core/store/modal.actions';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { TranslationService } from '@core/services/translation.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CommonModule, ActionModal, Toast, SplashScreen, TranslatePipe],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('app-solid');
  actionModal$: Observable<any>;
  infoModal$: Observable<any>;

  /** Splash screen state — skip on reload if session already seen */
  splashVisible = typeof window !== 'undefined' && !sessionStorage.getItem('app_loaded');
  splashFadeOut = false;
  private routerSub?: Subscription;

  private readonly router = inject(Router);
  private readonly ts = inject(TranslationService);

  constructor(private store: Store) {
    this.actionModal$ = this.store.select(selectActionModal);
    this.infoModal$ = this.store.select(selectInfoModal);
  }

  ngOnInit(): void {
    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.dismissSplash();
        // Clear any open action modal when navigating to a new page so stale
        // modals don't appear on the destination page.
        this.store.dispatch(hideActionModal());
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  private dismissSplash(): void {
    sessionStorage.setItem('app_loaded', '1');
    if (!this.splashVisible) return;
    this.splashFadeOut = true;
    setTimeout(() => {
      this.splashVisible = false;
    }, 500);
  }

  closeInfoModal(): void { this.store.dispatch(hideInfoModal()); }

  onModalCancel(modal: any) {
    this.store.dispatch(hideActionModal());
    if (modal.onCancel) {
      modal.onCancel();
    }
  }

  onModalConfirm(modal: any) {
    this.store.dispatch(hideActionModal());
    if (modal.onConfirm) {
      modal.onConfirm();
    }

    // When deferToast is true the onConfirm callback fires the toast itself
    // (e.g. delete operations that may return a 409 and must not show success prematurely)
    if (!modal?.deferToast) {
      const fallbackMessage = this.getDefaultToastMessage(modal?.actionState);
      const rawMessage = modal?.successToastMessage || fallbackMessage;
      const message = this.ts.translate(rawMessage);
      this.store.dispatch(showToast({ message, tone: 'success' }));
    }
  }

  private getDefaultToastMessage(actionState?: string): string {
    const normalized = String(actionState || '').trim().toLowerCase();
    if (normalized.includes('verwijder') || normalized.includes('delete') || normalized.includes('state.delete')) return 'action_modal.toast.default_delete';
    if (normalized.includes('bevestig') || normalized.includes('confirm') || normalized.includes('state.confirm')) return 'action_modal.toast.default_confirm';
    return 'action_modal.toast.default_action';
  }
}
